import { api } from "./api";

import type { SceneTemplate } from "@/lib/scene.types";

/**
 * Estado de las escenas de Eriol Go del usuario, persistido en la DB del backend
 * (`go_user_scenes`, por usuario). Es el origen de verdad de "qué diseño eligió"
 * y "qué escenas tiene instaladas" — reemplaza al viejo localStorage.
 *
 * `needsOnboarding` lo calcula el backend:
 *   (no existe fila para el userId) || (chosenDesignKey == null)
 */

export interface GoScenesState {
  chosenDesignKey: string | null;

  installedSlugs: string[];

  needsOnboarding: boolean;
}

export interface SaveGoScenesPayload {
  chosenDesignKey: string | null;

  installedSlugs: string[];
}

/** Mismo idiom que go.service.ts: el backend a veces envuelve en `{ data }`. */

function unwrap<T>(payload: T | { data: T }): T {
  if (
    payload &&
    typeof payload === "object" &&
    "data" in (payload as Record<string, unknown>) &&
    Object.keys(payload as Record<string, unknown>).length === 1
  ) {
    return (payload as { data: T }).data;
  }

  return payload as T;
}

class ScenesService {
  /** GET /go/scenes → diseño elegido + instaladas + needsOnboarding. */

  getGoScenes(): Promise<GoScenesState> {
    return api

      .authGet<GoScenesState | { data: GoScenesState }>("/go/scenes")

      .then(unwrap);
  }

  /** PUT /go/scenes → upsert de la fila del usuario; devuelve el estado guardado. */

  saveGoScenes(p: SaveGoScenesPayload): Promise<GoScenesState> {
    return api

      .authPut<GoScenesState | { data: GoScenesState }>("/go/scenes", p)

      .then(unwrap);
  }

  /**
   * GET /go/scenes/catalog → catálogo de escenas del móvil (Fuente/Coming
   * Soon/Cierre) desde la DB (`scene_templates`, surface=mobile). Reemplaza al
   * catálogo local hardcodeado. El backend resuelve `__ASSET_BASE__`; el
   * `__LANG__` de los thumbs lo resuelve el celu por idioma.
   */

  getScenesCatalog(): Promise<SceneTemplate[]> {
    return api

      .authGet<SceneTemplate[] | { data: SceneTemplate[] }>(
        "/go/scenes/catalog",
      )

      .then(unwrap);
  }
}

export const scenesService = new ScenesService();

export default scenesService;
